import { ViewOptions } from "@drillflo/drillcore/lib/2d/View";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDrill } from "./useDrill.tsx";
import { UserData } from "./useUser.tsx";
import Drill, { HashType } from "@drillflo/drillcore/lib/Drill";
import Engine, {
  EditMode,
  PaintMode,
  PaintShape,
} from "@drillflo/drillcore/lib/2d/Engine";
import { Rectangle } from "paper/dist/paper-core";
import React from "react";
import { FieldEditorData } from "./useFieldEditor.tsx";
import View from "@drillflo/drillcore/lib/2d/View.ts";

export interface DrillViewProps {
  slug: string;
  userData: UserData | null;
  fieldEditorData: FieldEditorData;
}

export interface PerformerPaintingProps {
  paintMode: PaintMode;
  paintShape: PaintShape;
  paintShapeEvenDimensions: boolean;
  drawSnapToSteps: number;
  performerPaintStepSeperation: number;
}

export interface DrillViewReturn {
  drill: Drill | null;
  engine: Engine | null;
  view: View | null;
  draw: () => Promise<void>;
  forceViewRecreate: () => void;
  canvasFullyLoaded: boolean;
  DrillCanvas: JSX.Element;
  performerPaintingProps: PerformerPaintingProps;
  setPerformerPaintingProps: (
    value: React.SetStateAction<PerformerPaintingProps>
  ) => void;
  viewOptions: ViewOptions;
  updateViewOptions: (newOptions: Partial<ViewOptions>) => Promise<void>;
  confirmShape: () => void;
  cancelShape: () => void;
}

export default function DrillView({
  slug,
  userData,
  fieldEditorData,
}: DrillViewProps) {
  /** ---- Refs & States ---- */
  const canvas = useRef<HTMLCanvasElement>(null);
  const [canvasWidth, setCanvasWidth] = useState(window.innerWidth * 1);
  const [canvasHeight, setCanvasHeight] = useState(window.innerHeight * 1);
  const [performerPaintingProps, setPerformerPaintingProps] =
    useState<PerformerPaintingProps>({
      paintMode: PaintMode.DRAW,
      paintShape: PaintShape.CIRCLE,
      paintShapeEvenDimensions: false,
      drawSnapToSteps: 1,
      performerPaintStepSeperation: -1,
    });

  /** ---- Hooks ---- */
  const {
    drill,
    engine,
    draw,
    forceViewRecreate,
    view,
    viewOptions,
    updateViewOptions,
  } = useDrill(canvas);

  const scaleCanvas = useCallback(() => {
    if (!canvas.current || !view) return;

    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const newCanvasWidth = Math.round(screenWidth * 0.8);
    const newCanvasHeight = Math.round(screenHeight * 0.8);

    // Handle device pixel ratio for high DPI displays

    // Set the actual canvas buffer size
    canvas.current.width = newCanvasWidth;
    canvas.current.height = newCanvasHeight;

    canvas.current.style.width = `${newCanvasWidth}px`;
    canvas.current.style.height = `${newCanvasHeight}px`;

    setCanvasWidth(newCanvasWidth);
    setCanvasHeight(newCanvasHeight);

    // Update bounds to match new canvas size while maintaining scale
    view.setBounds(
      new Rectangle(0, 0, newCanvasWidth, newCanvasHeight).scale(0.95)
    );
  }, [view]);

  const confirmShape = useCallback(() => {
    if (!engine || !drill) return;
    engine.confirmShape(drill);
    draw();
  }, [engine, drill, draw]);

  const cancelShape = useCallback(() => {
    if (!engine) return;
    engine.cancelShape();
    draw();
  }, [engine, draw]);

  useEffect(() => {
    if (view) {
      view.setEditMode(fieldEditorData.editMode);
      const engine = view.getEngine();
      engine.paintMode = performerPaintingProps.paintMode;
      engine.paintShape = performerPaintingProps.paintShape;
      engine.paintShapeEvenDimensions =
        performerPaintingProps.paintShapeEvenDimensions;
      engine.drawSnapToSteps = performerPaintingProps.drawSnapToSteps;
      engine.performerPaintStepSeperation =
        performerPaintingProps.performerPaintStepSeperation;
    }
  }, [viewOptions, fieldEditorData, view, performerPaintingProps]);

  // Adjust the canvas size on initial load and window resize
  useEffect(() => {
    scaleCanvas(); // Initial scaling

    const handleResize = () => {
      setTimeout(() => {
        scaleCanvas();
      }, 100);
    };

    window.addEventListener("resize", handleResize); // Listen for window resize

    return () => {
      window.removeEventListener("resize", handleResize); // Clean up on component unmount
    };
  }, [scaleCanvas]);

  const DrillCanvas = (
    <canvas
      id="drillCanvas"
      data-tour="field-canvas"
      style={{
        width: `${canvasWidth}px`,
        height: `${canvasHeight}px`,
      }}
      className="bg-white w-full rounded-lg shadow-md border border-gray-200"
      ref={canvas}
    ></canvas>
  );

  return {
    drill,
    view,
    engine,
    draw,
    forceViewRecreate,
    DrillCanvas,
    performerPaintingProps,
    setPerformerPaintingProps,
    viewOptions,
    updateViewOptions,
    confirmShape,
    cancelShape,
  } as DrillViewReturn;
}
